import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  buildRideMetadataAfterStationStructureDrySave,
  castErrorResult,
  ConfirmResult,
  createErrorResult,
  createSuccessResultVoid,
  errors,
  IRideDto,
  isStationRouteWaypointStation,
  IStationRideProps,
  PassengerDeleteResultEnum,
  RebuildRouteTypeEnum,
  removeEmptyStationFromRideDtoMetadata,
  removePassengersFromRideDtoMetadata,
  rideHasActiveSavedMlPath,
  ridePassengerDeleteBookingStateConfirmAtom,
  rideSelectedPassengersIdsAF,
  RideStationPanelMode,
  RideUpdateModel,
  SimpleResult,
  unique,
} from "@ryde/shared";

import {
  useModalConfirmsRide,
  useRebuildRoute,
  useRideDtoActions,
  useRideEdit,
  useRideEmptyStationsAllowed,
  useRideMlPathWaypointConfirm,
  useRidesProcessing,
} from "../../hooks";
import { useModalConfirm } from "../../hooks-singletons";

export type IUseRideStation = ReturnType<typeof useRideStation>;

export const useRideStation = (props: IStationRideProps) => {
  const { t } = useTranslation();
  const { station, rideDto, mode } = props;
  const [selectedPassengersIds, setSelectedPassengersIds] = useRecoilState(
    rideSelectedPassengersIdsAF(rideDto.id || 0),
  );
  const { updateEditingRideDto } = useRideDtoActions();
  const { removePassengersFromRide, removeEmptyStationFromRide } =
    useRidesProcessing();
  const { confirmRidePassengerDeleteBookingAction } = useModalConfirmsRide();
  const { confirmDialogResult } = useModalConfirm();
  const { saveRide } = useRideEdit();
  const { rebuildRideRoute } = useRebuildRoute();
  const { confirmWaypointChangeWithSavedMlPath } =
    useRideMlPathWaypointConfirm();
  const setPassDeleteBookingAction = useSetRecoilState(
    ridePassengerDeleteBookingStateConfirmAtom,
  );

  const {
    isRideWithEmptyStationsAllowed,
    isRideWithDuplicatedStationsAllowed,
  } = useRideEmptyStationsAllowed(mode, rideDto);

  const stationPassengers = useMemo(() => {
    return (
      rideDto?.metadata?.passengersInfo?.filter(
        pass => pass.from === station.id || pass.to === station.id,
      ) || []
    );
  }, [rideDto?.metadata?.passengersInfo, station.id]);

  const removeSelectedPassengersFromRide = useEvent(
    async (
      hoverPassId?: number | undefined,
    ): Promise<SimpleResult<RideUpdateModel | void>> => {
      const passengersIdsToRemove = hoverPassId
        ? unique([...selectedPassengersIds, hoverPassId])
        : unique([...selectedPassengersIds]);

      const passengersIdsToRemoveSet = new Set(passengersIdsToRemove);
      const passengersToRemove = rideDto?.metadata?.passengersInfo?.filter(p =>
        passengersIdsToRemoveSet.has(p.id),
      );

      if (!passengersToRemove?.length) {
        return new SimpleResult({
          error: t(errors.ride.notValidPassengersToRemove),
        });
      }

      const deletePassengerBookingAction =
        await confirmRidePassengerDeleteBookingAction({
          passengers: passengersToRemove,
          mode,
          ride: rideDto,
        });

      if (deletePassengerBookingAction === PassengerDeleteResultEnum.NoAction) {
        return new SimpleResult({
          error: t(errors.ride.removingPassengersFromRideCanceledByUser),
        });
      }

      if (mode === RideStationPanelMode.GRID_PANEL) {
        const removePassengersResult = await removePassengersFromRide?.(
          rideDto.id || 0,
          passengersIdsToRemove,
          deletePassengerBookingAction,
        );

        if (removePassengersResult.success) {
          setSelectedPassengersIds([]);
        }

        return removePassengersResult;
      }

      const metadataRecounted = removePassengersFromRideDtoMetadata(
        rideDto,
        passengersIdsToRemove,
      );
      const rideCheckedResult = await saveRide?.({
        rideDto,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        skipChangedValuesConfirmation: true,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked || (rideDto.id && rideChecked?.id !== rideDto.id)) {
        return new SimpleResult({
          error: t(errors.ride.invalidRideIDBadAPIResponse),
        });
      }

      updateEditingRideDto({
        ...rideChecked,
        metadata: buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDto.metadata,
          drySaveMetadata: rideChecked.metadata,
        }),
      });
      setSelectedPassengersIds(pass =>
        pass.filter(p => !passengersIdsToRemoveSet.has(p)),
      );

      passengersIdsToRemove.forEach(p => {
        setPassDeleteBookingAction(
          val => new Map(val.set(p, deletePassengerBookingAction)),
        );
      });
      setSelectedPassengersIds([]);

      return rideCheckedResult;
    },
  );
  const removeEmptyRideStation = useEvent(
    async (): Promise<SimpleResult<RideUpdateModel | void>> => {
      let rideDtoForRemove: Partial<IRideDto> = rideDto;
      const isWaypoint = isStationRouteWaypointStation(station);
      let skipMlPathByWaypoints = false;

      if (isWaypoint && rideHasActiveSavedMlPath(rideDtoForRemove.metadata)) {
        const confirmResult =
          await confirmWaypointChangeWithSavedMlPath(rideDtoForRemove);

        if (!confirmResult.confirmed) {
          return createErrorResult(errors.common.changedRejectedByUser);
        }

        skipMlPathByWaypoints = Boolean(confirmResult.nextMetadata);
        rideDtoForRemove = confirmResult.nextMetadata
          ? { ...rideDtoForRemove, metadata: confirmResult.nextMetadata }
          : rideDtoForRemove;
      }

      const confirmationResult = await confirmDialogResult({
        title: t("ride.RemoveStation"),
        message: t("ride.WantToRemoveStationConfirm"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        return createErrorResult(errors.common.changedRejectedByUser);
      }

      if (mode === RideStationPanelMode.GRID_PANEL) {
        const removePassengersResult = await removeEmptyStationFromRide?.({
          rideId: rideDtoForRemove.id || 0,
          emptyStationId: station.id || 0,
          secondaryId: station.secondaryId,
          skipMlPathByWaypoints,
        });

        return removePassengersResult;
      }

      const metadataRecounted = removeEmptyStationFromRideDtoMetadata({
        rideDto: rideDtoForRemove,
        station,
        isRideWithEmptyStationsAllowed,
      });

      if (isWaypoint) {
        const rideWithRemovedWaypoint: Partial<IRideDto> = {
          ...rideDtoForRemove,
          metadata: metadataRecounted,
        };

        const rebuildResult = await rebuildRideRoute(rideWithRemovedWaypoint, {
          rebuildType: RebuildRouteTypeEnum.KeepOrder,
          optimize: false,
        });

        if (!rebuildResult.success) {
          return castErrorResult(rebuildResult);
        }

        return createSuccessResultVoid();
      }

      const rideCheckedResult = await saveRide?.({
        rideDto: rideDtoForRemove,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        skipChangedValuesConfirmation: true,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked) {
        return new SimpleResult({
          error: t(errors.ride.invalidRideIDBadAPIResponse),
        });
      }

      if (rideDtoForRemove.id && rideChecked?.id !== rideDtoForRemove.id) {
        return new SimpleResult({
          error: t(errors.ride.invalidRideIDBadAPIResponse),
        });
      }

      updateEditingRideDto({
        ...rideChecked,
        metadata: buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDtoForRemove.metadata,
          drySaveMetadata: rideChecked.metadata,
        }),
      });

      return rideCheckedResult;
    },
  );

  const removeRideStation = useEvent(
    async (): Promise<SimpleResult<RideUpdateModel | void>> => {
      if (!stationPassengers?.length) {
        return removeEmptyRideStation();
      }

      const passengersIdsToRemove = stationPassengers.map(x => x.id);

      const deletePassengerBookingAction =
        await confirmRidePassengerDeleteBookingAction({
          passengers: stationPassengers,
          mode,
          ride: rideDto,
        });

      if (deletePassengerBookingAction === PassengerDeleteResultEnum.NoAction) {
        return new SimpleResult({
          error: t(errors.ride.removingStationFromRideCanceledByUser),
        });
      }

      if (mode === RideStationPanelMode.GRID_PANEL) {
        const removePassengersResult = await removePassengersFromRide?.(
          rideDto.id || 0,
          passengersIdsToRemove,
          deletePassengerBookingAction,
        );

        if (removePassengersResult.success) {
          setSelectedPassengersIds([]);
        }

        return removePassengersResult;
      }

      const metadataRecounted = removePassengersFromRideDtoMetadata(
        rideDto,
        passengersIdsToRemove,
      );

      const rideCheckedResult = await saveRide?.({
        rideDto,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        skipChangedValuesConfirmation: true,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked) {
        return new SimpleResult({
          error: t(errors.ride.invalidRideIDBadAPIResponse),
        });
      }

      if (rideDto.id && rideChecked?.id !== rideDto.id) {
        return new SimpleResult({
          error: t(errors.ride.invalidRideIDBadAPIResponse),
        });
      }

      updateEditingRideDto({
        ...rideChecked,
        metadata: buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDto.metadata,
          drySaveMetadata: rideChecked.metadata,
        }),
      });

      passengersIdsToRemove.forEach(p => {
        setPassDeleteBookingAction(
          val => new Map(val.set(p, deletePassengerBookingAction)),
        );
      });

      setSelectedPassengersIds([]);

      return rideCheckedResult;
    },
  );

  return {
    removeSelectedPassengersFromRide,
    isRideWithDuplicatedStationsAllowed,
    stationPassengers,
    removeRideStation,
  };
};
